import type { ParsedUrlQuery } from "querystring";

import type { FilterValue } from "~/types/pagination";

/**
 * Allowlist of SEO-relevant filter parameters
 * Only these params should be preserved in pagination URLs
 */
const ALLOWED_FILTER_PREFIXES = ["refinementList", "range", "sortBy", "ratingMenu"];

/**
 * Determines the canonical URL for a paginated PLP
 *
 * Rules (based on OP3 solution):
 * - Filtered pages: canonical points to parent category (no filters, no page)
 * - Clean pages: canonical is self-referential
 *   - Page 1: /category/
 *   - Page 2+: /category/?page=N
 *
 * @param baseUrl - Base category URL with trailing slash (e.g., /en-it/c/makeup/)
 * @param currentPage - Current page number (1-indexed)
 * @param hasFilters - Whether the page has active filters
 * @returns Canonical URL
 */
export function getCanonicalUrl(baseUrl: string, currentPage: number, hasFilters: boolean): string {
  // Filtered pages → canonical to parent category
  if (hasFilters) {
    return baseUrl;
  }

  // Clean pages → self-referential
  if (currentPage > 1) {
    return `${baseUrl}?page=${currentPage}`;
  }

  return baseUrl;
}

/**
 * Checks if the current PLP has active filters
 *
 * Uses the same allowlist as extractFilters to ensure consistency
 * Detects: refinementList, range, sortBy, ratingMenu
 *
 * @param query - Parsed URL query from Next.js router
 * @returns True if filters are active
 */
export function hasActiveFilters(query: ParsedUrlQuery): boolean {
  return Object.keys(query).some(isAllowedFilter);
}

/**
 * Checks if a query parameter key is an allowed filter
 */
function isAllowedFilter(key: string): boolean {
  return ALLOWED_FILTER_PREFIXES.some((prefix) => key.startsWith(prefix) || key.includes(`[${prefix}]`));
}

/**
 * Extracts filter parameters from URL query using allowlist
 *
 * Only extracts SEO-relevant filter params (refinementList, range, sortBy, ratingMenu)
 * Excludes tracking params (utm, gclid, fbclid, etc.)
 * Preserves multi-value arrays for multi-select filters
 *
 * @param query - Parsed URL query from Next.js router
 * @returns Record of filter parameters with multi-value support
 */
export function extractFilters(query: ParsedUrlQuery): Record<string, FilterValue> {
  const filters: Record<string, FilterValue> = {};

  Object.entries(query).forEach(([key, value]) => {
    // Only include allowed filter parameters
    if (isAllowedFilter(key) && value !== undefined) {
      filters[key] = value;
    }
  });

  return filters;
}
